'use client';

import {
  type MicErrorKind,
  type MicPermissionDeniedDetail,
  type Platform,
  queryMicPermission,
  subscribeMicError,
} from '@/lib/mic-permission';
import { Lock, MicOff, RefreshCcw, Settings, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/**
 * 2026-07-02 (Leon 反馈) — global mic permission modal.
 *
 * Mounted once at root layout. Listens for the `mic-permission-denied`
 * custom event fired by any getUserMedia caller via reportMicError().
 * When shown, gives platform-specific step-by-step instructions on how
 * to grant the mic permission.
 *
 * Why not just redirect to browser settings? Web pages cannot open
 * `chrome://settings/*` URLs — browser security invariant. The best we
 * can do is show clear instructions with visual cues.
 *
 * The 重试 button re-calls getUserMedia — succeeds if the user actually
 * went into settings + flipped the permission.
 */
export function MicPermissionModal() {
  const [detail, setDetail] = useState<MicPermissionDeniedDetail | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryHint, setRetryHint] = useState<string | null>(null);

  useEffect(
    () =>
      subscribeMicError((d) => {
        setDetail(d);
        setRetryHint(null);
      }),
    [],
  );

  const close = useCallback(() => {
    setDetail(null);
    setRetryHint(null);
  }, []);

  const retry = useCallback(async () => {
    setRetrying(true);
    setRetryHint(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Success — release the stream and dismiss the modal.
      for (const t of stream.getTracks()) t.stop();
      close();
    } catch {
      // Still denied. Check permission state to give a specific hint.
      const state = await queryMicPermission();
      if (state === 'denied') {
        setRetryHint('权限仍被浏览器记住为「拒绝」——需要按上面步骤去浏览器设置里手动改。');
      } else {
        setRetryHint('还是没能获得权限, 请再检查一下浏览器提示。');
      }
    } finally {
      setRetrying(false);
    }
  }, [close]);

  if (!detail) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-ink/60 backdrop-blur-sm p-4"
      aria-modal="true"
      aria-labelledby="mic-perm-title"
      // biome-ignore lint/a11y/useSemanticElements: modal container — role=dialog is the correct semantic here
      role="dialog"
    >
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 w-full h-full bg-transparent border-none cursor-default"
        onClick={close}
      />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-soft overflow-hidden">
        <div className="flex items-start gap-3 p-5 border-b border-soft/60">
          <div className="w-10 h-10 rounded-full bg-warmth/15 text-warmth-dark flex items-center justify-center flex-shrink-0">
            <MicOff size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="mic-perm-title" className="font-serif text-[17px] text-ink font-medium">
              {titleFor(detail.kind)}
            </h2>
            <p className="text-[13px] text-text-mute mt-0.5 leading-snug">
              {subtitleFor(detail.kind, detail.platform)}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-text-mute hover:text-ink transition-colors flex-shrink-0"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </div>

        {detail.kind === 'permission_denied' && (
          <div className="p-5">
            <div className="text-[13px] text-ink/85 leading-relaxed">
              {stepsFor(detail.platform)}
            </div>
          </div>
        )}

        {detail.kind === 'no_device' && (
          <div className="p-5 text-[13px] text-ink/85 leading-relaxed">
            没检测到麦克风。请确认手机 / 电脑连了可用的录音设备, 然后重试。
          </div>
        )}

        {detail.kind === 'busy' && (
          <div className="p-5 text-[13px] text-ink/85 leading-relaxed">
            麦克风被其他应用占用了 (通话 / 录音 / 其他浏览器 tab)。关掉那个应用再重试。
          </div>
        )}

        {detail.kind === 'insecure_context' && (
          <div className="p-5 text-[13px] text-ink/85 leading-relaxed">
            当前页面不是 HTTPS, 浏览器不允许访问麦克风。请通过 https:// 打开。
          </div>
        )}

        {detail.kind === 'unknown' && (
          <div className="p-5 text-[13px] text-ink/85 leading-relaxed space-y-2">
            <p>获取麦克风时出了个意外错误。请重试; 如果还不行, 可以先用文字聊天。</p>
            {detail.raw && (
              <p className="text-[11px] font-mono text-text-mute bg-cream/50 px-2 py-1 rounded">
                {detail.raw}
              </p>
            )}
          </div>
        )}

        {retryHint && <div className="px-5 pb-3 text-[12px] text-warmth-dark">{retryHint}</div>}

        <div className="flex gap-2 p-4 bg-cream/40 border-t border-soft/60">
          <button
            type="button"
            onClick={close}
            className="flex-1 px-4 py-2 text-[13px] text-ink border border-soft rounded-md bg-white hover:bg-cream/50 transition-colors"
          >
            关闭, 用文字
          </button>
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="flex-1 px-4 py-2 text-[13px] text-cream bg-ink rounded-md hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            <RefreshCcw size={14} className={retrying ? 'animate-spin' : ''} />
            {retrying ? '申请中…' : '重试'}
          </button>
        </div>
      </div>
    </div>
  );
}

function titleFor(kind: MicErrorKind): string {
  switch (kind) {
    case 'permission_denied':
      return '需要开启麦克风权限';
    case 'no_device':
      return '未检测到麦克风';
    case 'busy':
      return '麦克风被占用';
    case 'insecure_context':
      return 'HTTPS 才能用麦克风';
    default:
      return '麦克风出了点问题';
  }
}

function subtitleFor(kind: MicErrorKind, platform: Platform): string {
  if (kind !== 'permission_denied') return '';
  switch (platform) {
    case 'android_chrome':
      return '需要在 Chrome 里手动打开这个网站的麦克风权限';
    case 'ios_safari':
      return '需要在 Safari 里打开麦克风访问';
    case 'ios_other':
      return '需要在浏览器设置里打开麦克风';
    default:
      return '浏览器已记住拒绝, 需去设置里手动打开';
  }
}

function stepsFor(platform: Platform) {
  if (platform === 'android_chrome') {
    // 2026-07-02 (Leon 反馈追加) — Android 权限有两层:
    //   路径 A · 网站级: chrome://settings for chiefeco.ai
    //   路径 B · Android 系统级: Chrome App 本身必须先有 mic 权限
    // 路径 A 试过没用一定是路径 B 也被拒了, 所以两条一起给.
    return (
      <div className="space-y-4">
        <div>
          <div className="text-[12px] font-medium text-ink/85 mb-2 uppercase tracking-wider">
            路径 A · 从本页地址栏直接改（推荐先试）
          </div>
          <ol className="space-y-2.5">
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </span>
              <span>
                点顶部地址栏左边的 <Lock size={13} className="inline mx-0.5 -mt-0.5" />
                锁形图标
              </span>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </span>
              <span>
                选「权限 · Permissions」, 找 <b>麦克风 · Microphone</b>
              </span>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                3
              </span>
              <span>切成「允许 · Allow」, 回来点下面「重试」</span>
            </li>
          </ol>
        </div>

        <div className="pt-3 border-t border-soft/60">
          <div className="text-[12px] font-medium text-ink/85 mb-2 uppercase tracking-wider">
            路径 B · Android 系统设置（路径 A 无效时走这里）
          </div>
          <div className="text-[11px] text-text-mute mb-2 leading-snug">
            如果 Chrome App 本身没被 Android 授权用麦克风, 网站级权限改了也没用。
          </div>
          <ol className="space-y-2.5">
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </span>
              <span>
                打开手机「<b>设置 · Settings</b>」App
              </span>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </span>
              <span>
                找「<b>应用 · Apps</b>」→ <b>Chrome</b> (或你在用的浏览器 App)
              </span>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                3
              </span>
              <span>
                「<b>权限 · Permissions</b>」→ <b>麦克风 · Microphone</b> → 允许
              </span>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
                4
              </span>
              <span>回到本页, 按路径 A 检查网站级权限, 再点「重试」</span>
            </li>
          </ol>
          <div className="mt-2 text-[11px] text-text-mute leading-snug flex gap-1.5 items-start">
            <Settings size={11} className="mt-0.5 flex-shrink-0" />
            <span>MIUI / EMUI / OriginOS 位置可能叫「应用管理」/「权限管理」, 逻辑一样.</span>
          </div>
        </div>
      </div>
    );
  }
  if (platform === 'ios_safari' || platform === 'ios_other') {
    return (
      <ol className="space-y-3">
        <li className="flex gap-3">
          <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
            1
          </span>
          <span>打开 iOS 系统「设置」App</span>
        </li>
        <li className="flex gap-3">
          <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
            2
          </span>
          <span>找 Safari → 网站设置 → 麦克风 → 允许</span>
        </li>
        <li className="flex gap-3">
          <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
            3
          </span>
          <span>回到本页点「重试」</span>
        </li>
      </ol>
    );
  }
  return (
    <ol className="space-y-3">
      <li className="flex gap-3">
        <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
          1
        </span>
        <span>
          点地址栏左边的 <Lock size={13} className="inline mx-0.5 -mt-0.5" /> 图标
        </span>
      </li>
      <li className="flex gap-3">
        <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
          2
        </span>
        <span>找「麦克风」, 切成「允许」</span>
      </li>
      <li className="flex gap-3">
        <span className="w-5 h-5 rounded-full bg-ink text-cream text-[11px] font-mono flex items-center justify-center flex-shrink-0 mt-0.5">
          3
        </span>
        <span>刷新页面或直接点下面「重试」</span>
      </li>
    </ol>
  );
}
